// AppLauncherPanel (knowledge/09, cat 48.11–48.13) — the top-bar waffle's popover content: every
// app usable at the CURRENT level, each with an honest data-readiness signal. TopNav owns the
// trigger (waffle TbIconButton) and wraps this panel in the shared Popover, whose children mount
// only while open — so this panel's fetches are fetch-on-open by construction (no always-on cost).
//
// Row semantics (L1/L2, locked):
//  · mounted (real placement, shared useAppInstances source) → "Open" at the instance's MOUNT
//    node via resolveAppTarget — the same one-door target the sidebar row and Apps-tab card use.
//  · available + ● data ready → "Open" launches at the CURRENT node (buildAppUrl).
//  · available + ○ needs data → CTA ("Import → Data") navigates to THIS level's Data facet tab,
//    except producer apps whose manifest explicitly permits an empty first-use launch.
//  · readiness = manifest `dataReadiness.probe` (walled fetch at the node, non-empty array = ●),
//    else the generic fallback (canonical /sources at the node). Probe error → NO badge — an
//    unknown state is never rendered as ready or needs-import.
//  · Library is excluded — it is part of the workspace (its 4th facet tab, amendment #2).
import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Database } from "lucide-react";
import { SUBAPPS, appCanMountAt } from "@perlab/registry";
import { useContextChain } from "../state/ContextChain";
import { useMountScope } from "../state/useMountScope";
import { buildAppUrl } from "./ContextChain";
import { facetTarget, type ScopeIds, type ScopeLevel } from "../facets/registry";
import { resolveAppTarget } from "./appTarget";
import { useAppInstances, dedupeDeepestByApp } from "../state/appInstances";
import { appReadinessProbe, type Readiness } from "../state/useAppReadiness";
import { AppIconResolver } from "./AppIconResolver";
import { useEntitlements } from "../../lib/auth/EntitlementsContext";
import { ClientAccessTierBadge } from "./ClientAccessTierBadge";
import { placeAppInstance } from "../api/appInstances";
import { useToast } from "@perlab/ui";

export function AppLauncherPanel({ close }: { close: () => void }): React.ReactElement {
  const { workspaceId, clientId, engagementId, projectId } = useContextChain();
  const { scopeNodeId, currentLevel } = useMountScope();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasAppAccess } = useEntitlements();
  const [mountingAppId, setMountingAppId] = useState<string | null>(null);

  // URL-derived scope facts are immediate. ContextChain is synchronized in an effect and can lag
  // by a render after navigation, which previously made a client/engagement/project app briefly
  // disappear or probe the preceding node.
  const level: ScopeLevel = currentLevel;
  const ids: ScopeIds = { ws: workspaceId, client: clientId, eng: engagementId, project: projectId };
  const nodeId = scopeNodeId || projectId || engagementId || clientId || workspaceId;

  const instancesState = useAppInstances(nodeId);
  const mounted = (instancesState.kind === "ready" ? dedupeDeepestByApp(instancesState.data) : [])
    .filter((i) => i.app_id !== "library" && hasAppAccess(i.app_id));
  const mountedByApp = new Map(mounted.map((i) => [i.app_id, i]));

  // Level-usable apps: mounted here (any placement) OR manifest-mountable at this level.
  const apps = SUBAPPS.filter((a) => hasAppAccess(a.id) && a.id !== "library" && (mountedByApp.has(a.id) || appCanMountAt(a, level)));

  // Readiness per app — fetch-on-open (this panel mounts only while the popover is open).
  const [readiness, setReadiness] = useState<Record<string, Readiness>>({});
  useEffect(() => {
    let cancelled = false;
    setReadiness(Object.fromEntries(apps.map((a) => [a.id, "pending" as Readiness])));
    for (const a of apps) {
      if (a.dataReadiness?.launchWithDemoData) continue;
      // MB5 §6: the SINGLE shared readiness path (manifest-driven governed, else legacy probe) — the
      // launcher no longer duplicates the probe rules.
      appReadinessProbe(a, nodeId)
        .then((state) => { if (!cancelled) setReadiness((r) => ({ ...r, [a.id]: state })); })
        .catch(() => {
          if (cancelled) return;
          // honest unknown (denial/sealed/error) — drop the badge rather than fake a state
          setReadiness((r) => {
            const { [a.id]: _dropped, ...rest } = r;
            return rest;
          });
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const go = (target: object): void => {
    close();
    navigate(target as never);
  };

  const mountAndOpen = async (appId: string, appName: string, target: object): Promise<void> => {
    if (!nodeId || mountingAppId) return;
    setMountingAppId(appId);
    try {
      await placeAppInstance(appId, nodeId);
      toast(`${appName} mounted.`, "success");
      go(target);
    } catch (error) {
      toast(`Could not mount ${appName}: ${error instanceof Error ? error.message : "unknown error"}`, "danger");
    } finally {
      setMountingAppId(null);
    }
  };

  return (
    <div data-app="app-launcher" className="w-80">
      <p className="px-3 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
        App launcher · {level}
      </p>
      {apps.length === 0 ? (
        <p className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">No apps usable at this level.</p>
      ) : (
        <ul>
          {apps.map((a) => {
            const inst = mountedByApp.get(a.id);
            const state = readiness[a.id];
            const launchesWithDemoData = a.dataReadiness?.launchWithDemoData === true;
            const openTarget = inst
              ? resolveAppTarget({ workspaceId, scopeNodeId: inst.scope_node_id, appId: a.id, readerScopeNodeId: nodeId })
              : buildAppUrl(ids.ws, ids.client, ids.eng, ids.project, a.id);
            // MB5 §6: partial (some-but-not-all required inputs, manifest allows partial) is openable.
            const launchRequiresData = a.dataReadiness?.requiredForLaunch !== false;
            const canOpen = (
              launchesWithDemoData
              || Boolean(inst)
              || !launchRequiresData
              || state === "ready"
              || state === "partial"
            );
            return (
              <li key={a.id} className="border-t border-[var(--color-border-subtle)] first:border-t-0">
                <div className="flex items-center gap-2.5 px-3 py-2.5">
                  <span className="shrink-0 text-[var(--color-text-secondary)]"><AppIconResolver name={a.icon} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="min-w-0 truncate text-sm font-medium text-[var(--color-text-primary)]">{a.name}</span>
                      {a.permissions?.clientAccessTier && (
                        <ClientAccessTierBadge tier={a.permissions.clientAccessTier} />
                      )}
                    </span>
                    <span className="block text-[11px] text-[var(--color-text-secondary)]">
                      {inst ? "mounted" : "available"}
                      {launchesWithDemoData && <> · <span className="text-[var(--color-accent)]">clickflow demo</span></>}
                      {!launchesWithDemoData && state === "ready" && <> · <span className="text-[var(--color-accent)]">● data ready</span></>}
                      {!launchesWithDemoData && state === "partial" && <> · <span className="text-[var(--color-warning-500)]">◐ partial data</span></>}
                      {!launchesWithDemoData && state === "needs" && (
                        <> · ○ {launchRequiresData ? "needs data" : "no sessions yet"}</>
                      )}
                      {!launchesWithDemoData && state === "pending" && <> · checking…</>}
                    </span>
                  </span>
                  {canOpen ? (
                    <button
                      type="button"
                      onClick={() => inst || launchesWithDemoData
                        ? go(openTarget)
                        : void mountAndOpen(a.id, a.name, openTarget)}
                      disabled={mountingAppId !== null}
                      className="shrink-0 inline-flex items-center gap-1 rounded-[2px] border border-[var(--color-border-subtle)] px-2 py-1 text-xs text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
                    >
                      {inst || launchesWithDemoData
                        ? "Open"
                        : mountingAppId === a.id ? "Mounting…" : "Mount and open"} <ArrowRight size={12} aria-hidden />
                    </button>
                  ) : state === "needs" && launchRequiresData ? (
                    <button
                      type="button"
                      onClick={() => go(facetTarget(level, ids, "/data"))}
                      className="shrink-0 inline-flex items-center gap-1 rounded-[2px] border border-[var(--color-border-subtle)] px-2 py-1 text-xs text-[var(--color-accent)] hover:bg-[var(--color-surface-muted)]"
                    >
                      <Database size={12} aria-hidden /> Import → Data
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
